import { GetServerSideProps } from 'next';
import { pick, sortBy } from 'lodash-es';
import { BuybackProduct } from '@lambda/apis/src/dynamoDb/types';
import pMap from '@lambda/commons/p-map';
import React from 'react';
import { BuybackSGConfig } from '@/settings/configs/buyback/sg/type';
import configs from '@/settings/configs/buyback/sg';
import settings from '@/settings';
import Buyback from '@/components/buyback';
import { DeviceList } from '@/components/buyback/ProductsList';
import { getListings } from '@/lib/backend/buybackListings';
import findPimProduct from '@/lib/backend/findPimProduct';
import { pluralize } from '@/lib/pluralize';
import { REDIRECT_HOME } from '@/lib/common';

type MinimalBuybackProduct = Pick<
  BuybackProduct,
  'image' | 'model' | 'slug' | 'condition' | 'brand' | 'category'
> & { releaseDate: string };

type Props = {
  config: BuybackSGConfig;
  listings: Array<MinimalBuybackProduct>;
};

const REDIRECT_BUYBACK_US = {
  redirect: { permanent: false, destination: '/buyback' },
};

export const getServerSideProps: GetServerSideProps<Props> = async (event) => {
  if (settings.store === 'reebelo-us') return REDIRECT_BUYBACK_US;
  if (!(settings.store === 'quista')) return REDIRECT_HOME;

  const { params } = event;
  const handle = params?.handle as string;

  if (handle == null) return REDIRECT_HOME;

  const pageConfig = configs.find((cfg) => cfg.handle === handle);

  if (pageConfig == null) return REDIRECT_HOME;

  const listings =
    pageConfig.type === 'listing'
      ? await getListings({
          brand: pageConfig.brand.toLowerCase(),
          type: pageConfig.category,
        })
      : [];

  // ingest release date from PIM for sorting
  const withPIMListing = await pMap(listings, async (list) => {
    const psku = list.pimPSKU;
    const pim = await findPimProduct(psku);

    return {
      ...list,
      releaseDate: pim?.releaseDate || '1990-01-01',
    };
  });

  return {
    props: {
      config: pageConfig,
      listings: sortBy(
        withPIMListing.map((item) =>
          pick(item, [
            'image',
            'model',
            'slug',
            'condition',
            'brand',
            'category',
            'releaseDate',
          ]),
        ),
        ['releaseDate', 'slug'],
      ).reverse(),
    },
  };
};

const convertListingToDevice = (
  listings: Array<MinimalBuybackProduct>,
  condition: 'new' | 'used',
): Array<DeviceList> =>
  listings.map((listingItem) => ({
    image: listingItem.image,
    text: listingItem.model,
    releaseDate: listingItem.releaseDate,
    link: `/buyback-form/${
      listingItem.slug
    }?brand=${listingItem.brand.toLowerCase()}&category=${
      listingItem.category
    }&condition=${condition}`,
  }));

const getPriceList = (config: BuybackSGConfig) => {
  if (
    config.type !== 'listing' ||
    (config.priceListNew == null && config.priceListUsed == null)
  )
    return undefined;

  return {
    new: config.priceListNew,
    used: config.priceListUsed,
  };
};

export default function BuybackPage({ config, listings }: Props) {
  const navigationList = config.type === 'navigation' ? config.content : [];
  const isMainNav =
    config.type === 'navigation' && config.handle === 'sell-electronic';
  const deviceList =
    config.type === 'listing'
      ? {
          new:
            config.handle === 'sell-macbook'
              ? []
              : convertListingToDevice(listings, 'new'),
          used: convertListingToDevice(listings, 'used'),
        }
      : undefined;
  const priceList = getPriceList(config);
  const brandName = config.type === 'listing' ? config.brand : '';

  const getCustomSEOText = ({ handle, title, seoText }: BuybackSGConfig) => {
    const lowerCaseHandle = handle.toLowerCase();

    if (lowerCaseHandle === 'sell-electronic') {
      return {
        title:
          'Sell Your Electronics on Reebelo - Quick & Easy BuyBack Process',
        description: seoText,
      };
    }

    if (
      lowerCaseHandle === 'sell-tablet' ||
      lowerCaseHandle === 'sell-phone' ||
      lowerCaseHandle === 'sell-smartwatches'
    ) {
      return {
        title: `Sell & Upgrade Your ${pluralize(title)}`,
        description: seoText,
      };
    }

    return {
      title: `Sell Your New & Used ${pluralize(title)} on Reebelo`,
      description: seoText,
    };
  };

  return (
    <Buyback
      key={config.handle}
      bannerData={{ heading: `SELL YOUR ${config.title.toUpperCase()} TODAY` }}
      brand={brandName}
      brandShort={brandName}
      title={config.title}
      navigationList={navigationList}
      isMainNav={isMainNav}
      typeFormDevicesList={deviceList}
      priceTableList={priceList}
      customSEOText={getCustomSEOText(config)}
    />
  );
}
